import React, { useState, useEffect } from 'react';
import { 
  Wrench, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ArrowDown, 
  Gauge, 
  Layers, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Truck,
  ArrowRight,
  Package,
  Activity,
  AlertCircle
} from 'lucide-react';
import api from '../utils/api';

interface DashboardStats {
  activePreset: 'SETORES_GARGALOS' | 'SLAS_ENTREGAS' | 'VOLUME_METAS';
  dateRangeDays: number;
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  emergencyCount: number;
  priorityCount: number;
  overdueCount: number;
  rateOverdue: number;
  productionRate: number;
  topProducts: Array<{ name: string; quantity: number }>;
  sectorTimes: {
    corte: number;
    dobra: number;
    solda: number;
    acabamento: number;
    embalagem: number;
    instalacao: number;
  };
  upcomingDeliveries: Array<{
    id: number;
    number: number;
    customerName: string;
    departure: string;
    daysLeft: number;
    isEmergence: boolean;
    isPriority: boolean;
    statusName: string;
  }>;
  averageTimeFormatted?: string;
  bottleneckSector?: string;
}

export default function HomeView() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [overridePreset, setOverridePreset] = useState<'SETORES_GARGALOS' | 'SLAS_ENTREGAS' | 'VOLUME_METAS' | null>(null);
  const [selectedDays, setSelectedDays] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#analysis-filter-dropdown') && !target.closest('.dropdown-menu')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const fetchStats = async (daysOverride?: number) => {
    setLoading(true);
    try {
      const daysParam = daysOverride !== undefined ? daysOverride : (selectedDays || '');
      const url = daysParam ? `/dashboard-stats?days=${daysParam}` : '/dashboard-stats';
      const response = await api.get<DashboardStats>(url);
      if (response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard stats', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Auto refresh a cada 15 segundos para dar a cara de monitor de fábrica dinâmico
    const interval = setInterval(() => {
      fetchStats();
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedDays]);

  if (loading && !stats) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5 my-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Carregando painel...</span>
        </div>
        <p className="text-sm font-mono text-muted">Carregando métricas em tempo real do chão de fábrica...</p>
      </div>
    );
  }

  // Fallback defaults se der algum erro
  const currentStats = stats || {
    activePreset: 'SETORES_GARGALOS' as const,
    dateRangeDays: 7,
    totalOrders: 6,
    pendingOrders: 4,
    completedOrders: 2,
    emergencyCount: 1,
    priorityCount: 1,
    overdueCount: 1,
    rateOverdue: 25,
    productionRate: 33,
    topProducts: [
      { name: 'Coifa Alpha (Industrial)', quantity: 4 },
      { name: 'Duto inox 4302B - 5mm', quantity: 2 },
      { name: 'Filtro Inercial Inox', quantity: 1 }
    ],
    sectorTimes: {
      corte: 42,
      dobra: 30,
      solda: 58,
      acabamento: 43,
      embalagem: 15,
      instalacao: 90
    },
    upcomingDeliveries: [],
    averageTimeFormatted: '4h44min',
    bottleneckSector: 'Solda'
  };

  const activePreset = overridePreset || currentStats.activePreset;

  return (
    <div className="fade-in px-1" id="performance-dashboard-view">
      
      {/* Top Header Section of View */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3 border-bottom pb-3 font-sans">
        <div>
          <h1 className="h3 fw-bold text-dark font-display mb-1 text-lowercase" style={{ letterSpacing: '-0.5px' }}>
            performance da produção
          </h1>
          <p className="text-xs font-mono text-muted mb-0">
            Painel Central • Modo Ativo: {' '}
            <span className="fw-bold text-dark uppercase font-mono">
              {activePreset === 'SETORES_GARGALOS' && 'Setorização & Gargalos'}
              {activePreset === 'SLAS_ENTREGAS' && 'SLA, Prazos & Entregas'}
              {activePreset === 'VOLUME_METAS' && 'Volume & Mix de Metas'}
            </span>
          </p>
        </div>
        
        {/* Toggle Preset Override Controls */}
        <div className="d-flex align-items-center flex-wrap gap-2">
          <div className="btn-group shadow-xs rounded border bg-white p-1">
            <button 
              onClick={() => setOverridePreset('SETORES_GARGALOS')}
              className={`btn btn-xs font-mono py-1 px-2.5 text-xxs ${activePreset === 'SETORES_GARGALOS' ? 'btn-dark text-white rounded' : 'btn-light border-0 text-muted bg-transparent'}`}
              type="button"
            >
              Chão de Fábrica
            </button>
            <button 
              onClick={() => setOverridePreset('SLAS_ENTREGAS')}
              className={`btn btn-xs font-mono py-1 px-2.5 text-xxs ${activePreset === 'SLAS_ENTREGAS' ? 'btn-dark text-white rounded' : 'btn-light border-0 text-muted bg-transparent'}`}
              type="button"
            >
              SLAs & Prazos
            </button>
            <button 
              onClick={() => setOverridePreset('VOLUME_METAS')}
              className={`btn btn-xs font-mono py-1 px-2.5 text-xxs ${activePreset === 'VOLUME_METAS' ? 'btn-dark text-white rounded' : 'btn-light border-0 text-muted bg-transparent'}`}
              type="button"
            >
              Resultados
            </button>
          </div>

          <div className="dropdown">
            <button 
              type="button"
              className={`btn bg-white border d-flex align-items-center gap-2 text-muted shadow-sm py-1.5 px-3 rounded dropdown-toggle border-light-subtle ${isDropdownOpen ? 'show' : ''}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
              id="analysis-filter-dropdown"
            >
              <Calendar size={15} className="text-secondary" />
              <div className="text-start" style={{ fontSize: '0.8rem', lineHeight: '1.1' }}>
                <div className="font-mono text-muted text-xxs tracking-wider uppercase">Filtro de Análise</div>
                <div className="font-sans fw-bold text-dark">Últimos {selectedDays || currentStats.dateRangeDays} dias</div>
              </div>
            </button>
            <ul 
              className={`dropdown-menu dropdown-menu-end shadow-sm ${isDropdownOpen ? 'show' : ''}`} 
              aria-labelledby="analysis-filter-dropdown"
              style={{ 
                display: isDropdownOpen ? 'block' : 'none', 
                position: 'absolute', 
                right: 0, 
                top: '100%',
                zIndex: 1050
              }}
            >
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${(selectedDays || currentStats.dateRangeDays) === 7 ? 'active fw-bold' : ''}`} 
                  onClick={() => { setSelectedDays(7); fetchStats(7); setIsDropdownOpen(false); }}
                  type="button"
                >
                  Últimos 7 dias (Semanal)
                </button>
              </li>
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${(selectedDays || currentStats.dateRangeDays) === 15 ? 'active fw-bold' : ''}`} 
                  onClick={() => { setSelectedDays(15); fetchStats(15); setIsDropdownOpen(false); }}
                  type="button"
                >
                  Últimos 15 dias (Quinzena)
                </button>
              </li>
              <li>
                <button 
                  className={`dropdown-item font-sans text-xs ${(selectedDays || currentStats.dateRangeDays) === 30 ? 'active fw-bold' : ''}`} 
                  onClick={() => { setSelectedDays(30); fetchStats(30); setIsDropdownOpen(false); }}
                  type="button"
                >
                  Últimos 30 dias (Mês Completo)
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* RENDER LAYOUT BASEADO NO PRESET ATIVO */}
      
      {/* 1. LAYOUT SETORES & GARGALOS (CHÃO DE FÁBRICA) */}
      {activePreset === 'SETORES_GARGALOS' && (
        <div className="animate-fade-in">
          {/* Row 1: KPI Cards */}
          <div className="row g-4 mb-4">
            {/* Card 1: Produção Média */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="p-3 bg-primary bg-opacity-10 text-primary rounded flex-shrink-0 d-flex align-items-center justify-content-center">
                    <Activity size={32} strokeWidth={2.5} />
                  </div>
                  <div className="text-end">
                    <h2 className="fw-black text-dark font-display m-0" style={{ fontSize: '2.5rem', lineHeight: '1' }}>
                      {currentStats.totalOrders} pedidos
                    </h2>
                    <span className="text-light-emphasis text-base font-sans mt-1 d-block font-bold">Produção Acumulada</span>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end pt-3 border-top border-light border-opacity-10 text-xs">
                  <div>
                    <span className="text-muted d-block font-mono text-xxs uppercase tracking-wider">Período de Análise</span>
                    <span className="fw-bold text-dark font-sans" style={{ fontSize: '0.9rem' }}>{currentStats.dateRangeDays} dias operando</span>
                  </div>
                  <span className="text-secondary opacity-75 font-mono d-flex align-items-center gap-0.5" style={{ fontSize: '0.9rem' }}>
                    Em dia
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Tempo Médio Geral */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="p-3 bg-danger bg-opacity-10 text-danger rounded flex-shrink-0 d-flex align-items-center justify-content-center">
                    <Clock size={32} strokeWidth={2.5} />
                  </div>
                  <div className="text-end">
                    <h2 className="fw-black text-dark font-display m-0" style={{ fontSize: '2.5rem', lineHeight: '1' }}>
                      {currentStats.averageTimeFormatted || '0 min'}
                    </h2>
                    <span className="text-light-emphasis text-base font-sans mt-1 d-block font-bold">Tempo Médio Completo</span>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end pt-3 border-top border-light border-opacity-10 text-xs">
                  <div>
                    <span className="text-muted d-block font-mono text-xxs uppercase tracking-wider">Indicador de Gargalo</span>
                    <span className="fw-bold text-danger font-sans uppercase font-mono text-xxs">
                      {currentStats.bottleneckSector || 'Sem Gargalos'}
                    </span>
                  </div>
                  <span className="text-danger opacity-75 font-mono d-flex align-items-center gap-0.5" style={{ fontSize: '0.9rem' }}>
                    {currentStats.bottleneckSector && currentStats.bottleneckSector !== 'Nenhum' ? 'Ajustes de Velocidade' : 'Fluxo Estável'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Setores Progress Bar List & Mais Vendidos */}
          <div className="row g-4 mb-4 font-sans">
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                  <h5 className="fw-bold font-sans text-dark m-0" style={{ fontSize: '1rem' }}>
                    Tempo Médio por Setor Produtivo
                  </h5>
                  <span className="badge bg-light text-dark font-mono text-xxs">Chão de Fábrica</span>
                </div>

                <div className="d-flex flex-column gap-3">
                  {/* Progress: Corte */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark font-sans text-sm">Corte</span>
                      <span className="fw-bold text-dark font-mono text-sm">{currentStats.sectorTimes.corte} min.</span>
                    </div>
                    <div className="progress rounded-pill bg-light" style={{ height: '10px' }}>
                      <div className="progress-bar bg-warning rounded-pill" role="progressbar" style={{ width: `${Math.min(currentStats.sectorTimes.corte, 100)}%` }}></div>
                    </div>
                  </div>

                  {/* Progress: Dobra */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark font-sans text-sm">Dobra</span>
                      <span className="fw-bold text-dark font-mono text-sm">{currentStats.sectorTimes.dobra} min.</span>
                    </div>
                    <div className="progress rounded-pill bg-light" style={{ height: '10px' }}>
                      <div className="progress-bar bg-primary rounded-pill" role="progressbar" style={{ width: `${Math.min(currentStats.sectorTimes.dobra, 100)}%` }}></div>
                    </div>
                  </div>

                  {/* Progress: Solda */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark font-sans text-sm">Soldas</span>
                      <span className="fw-bold text-danger font-mono text-sm">{currentStats.sectorTimes.solda} min.</span>
                    </div>
                    <div className="progress rounded-pill bg-light" style={{ height: '10px' }}>
                      <div className="progress-bar bg-danger rounded-pill" role="progressbar" style={{ width: `${Math.min(currentStats.sectorTimes.solda, 100)}%` }}></div>
                    </div>
                  </div>

                  {/* Progress: Acabamento */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark font-sans text-sm">Acabamento</span>
                      <span className="fw-bold text-dark font-mono text-sm">{currentStats.sectorTimes.acabamento} min.</span>
                    </div>
                    <div className="progress rounded-pill bg-light" style={{ height: '10px' }}>
                      <div className="progress-bar bg-success rounded-pill" role="progressbar" style={{ width: `${Math.min(currentStats.sectorTimes.acabamento, 100)}%` }}></div>
                    </div>
                  </div>

                  {/* Progress: Embalagem & Logística */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark font-sans text-sm">Embalagem</span>
                      <span className="fw-bold text-dark font-mono text-sm">{currentStats.sectorTimes.embalagem} min.</span>
                    </div>
                    <div className="progress rounded-pill bg-light" style={{ height: '10px' }}>
                      <div className="progress-bar bg-info rounded-pill" role="progressbar" style={{ width: `${Math.min(currentStats.sectorTimes.embalagem, 100)}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Catalog of Selling Rank */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                  <h5 className="fw-bold font-sans text-dark m-0" style={{ fontSize: '1rem' }}>
                    Mix de Itens Ativos na Fábrica
                  </h5>
                  <span className="badge bg-light text-dark font-mono text-xxs">Demanda Comercial</span>
                </div>

                <div className="table-responsive">
                  <table className="table table-borderless table-align-middle mb-0 text-dark">
                    <thead>
                      <tr className="border-bottom border-light">
                        <th className="px-0 pb-2 text-muted fw-bold font-sans text-xs">Modelo da Coifa / Peça</th>
                        <th className="text-end pb-2 text-muted fw-bold font-sans text-xs pe-2">Unidades Totais</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentStats.topProducts.map((prod, idx) => (
                        <tr key={idx} className="border-bottom border-light border-opacity-50">
                          <td className="px-0 py-2">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-light rounded border d-flex align-items-center justify-content-center" style={{ width: '55px', height: '38px' }}>
                                <svg viewBox="0 0 64 36" width="36" height="22" fill="none" stroke="#2f66aa" strokeWidth="2">
                                  <path d="M 12 30 L 22 8 L 42 8 L 52 30 Z" fill="#b0b0b0" stroke="#444" />
                                  <rect x="25" y="0" width="14" height="8" fill="#d0d0d0" stroke="#444" />
                                </svg>
                              </div>
                              <div>
                                <span className="fw-semibold text-primary font-sans d-block" style={{ fontSize: '0.88rem' }}>
                                  {prod.name}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="text-end fw-black text-dark font-sans font-bold pe-2" style={{ fontSize: '1.05rem' }}>
                            {prod.quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. LAYOUT SLAs, PRAZOS & ENTREGAS (COMPROMISSOS) */}
      {activePreset === 'SLAS_ENTREGAS' && (
        <div className="animate-fade-in font-sans">
          {/* Row 1: KPI Cards */}
          <div className="row g-4 mb-4">
            {/* Card 1: Taxa de Atrasos */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border border-danger border-opacity-15 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="p-3 bg-danger bg-opacity-10 text-danger rounded flex-shrink-0 d-flex align-items-center justify-content-center">
                    <AlertTriangle size={32} strokeWidth={2.5} />
                  </div>
                  <div className="text-end">
                    <h2 className="fw-black text-danger font-display m-0" style={{ fontSize: '2.5rem', lineHeight: '1' }}>
                      {currentStats.rateOverdue}%
                    </h2>
                    <span className="text-light-emphasis text-base font-sans mt-1 d-block font-bold">Taxa de Atraso de Prazos</span>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end pt-3 border-top border-light border-opacity-10 text-xs text-dark">
                  <div>
                    <span className="text-muted d-block font-mono text-xxs uppercase tracking-wider">Metas de SLA Mínimo</span>
                    <span className="fw-bold text-dark font-sans" style={{ fontSize: '0.9rem' }}>Inferior a 15%</span>
                  </div>
                  <span className="fw-bold text-danger font-mono d-flex align-items-center gap-0.5" style={{ fontSize: '0.9rem' }}>
                    Abaixo da Meta
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Alertas de Emergência */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border border-danger border-opacity-20 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="p-3 bg-danger bg-opacity-10 text-danger rounded flex-shrink-0 d-flex align-items-center justify-content-center">
                    <AlertCircle size={32} strokeWidth={2.5} />
                  </div>
                  <div className="text-end">
                    <h2 className="fw-black text-dark font-display m-0" style={{ fontSize: '2.5rem', lineHeight: '1' }}>
                      {currentStats.overdueCount} atrasados
                    </h2>
                    <span className="text-light-emphasis text-base font-sans mt-1 d-block font-bold">Gargalos Críticos Vencidos</span>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end pt-3 border-top border-light border-opacity-10 text-xs text-dark">
                  <div>
                    <span className="text-muted d-block font-mono text-xxs uppercase tracking-wider">Marcados como Emergência</span>
                    <span className="fw-bold text-danger font-sans font-mono text-xxs uppercase">{currentStats.emergencyCount} Ativos no Setor</span>
                  </div>
                  <span className="text-muted font-mono d-flex align-items-center gap-0.5" style={{ fontSize: '0.9rem' }}>
                    Ação Imediata
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: SLA Countdown Table */}
          <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 mb-4 text-dark">
            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
              <div>
                <h5 className="fw-bold font-sans text-dark m-0" style={{ fontSize: '1rem' }}>
                  SLA Tracker - Contagem Regressiva de Entregas
                </h5>
                <p className="text-xxs text-muted mb-0 font-mono">Pedidos em produção ordenados por criticidade de entrega</p>
              </div>
              <span className="badge bg-danger bg-opacity-10 text-danger font-mono text-xxs uppercase">prioritários e vencidos</span>
            </div>

            <div className="table-responsive d-none d-md-block">
              <table className="table table-borderless table-align-middle mb-0 text-dark">
                <thead>
                  <tr className="border-bottom border-light">
                    <th className="text-muted text-xs">Pedido</th>
                    <th className="text-muted text-xs">Cliente / Destino</th>
                    <th className="text-muted text-xs">Frente Atual</th>
                    <th className="text-muted text-xs text-center">Data Estipulada</th>
                    <th className="text-muted text-xs text-end">Balanço de Dias</th>
                  </tr>
                </thead>
                <tbody>
                  {currentStats.upcomingDeliveries && currentStats.upcomingDeliveries.length > 0 ? (
                    currentStats.upcomingDeliveries.map((item, idx) => {
                      const isOverdue = item.daysLeft < 0;
                      const isCritical = item.daysLeft <= 1 || item.isEmergence;
                      return (
                        <tr key={idx} className="border-bottom border-light border-opacity-50 align-middle">
                          <td className="py-2.5">
                            <span className="badge bg-secondary font-mono">#{item.number}</span>
                            {item.isEmergence && (
                              <span className="badge bg-danger text-white font-mono text-xxs ms-1 uppercase animate-pulse">Emergência</span>
                            )}
                            {item.isPriority && (
                              <span className="badge bg-warning text-dark font-mono text-xxs ms-1 uppercase">Prioridade</span>
                            )}
                          </td>
                          <td>
                            <div className="fw-bold text-dark text-sm leading-tight">{item.customerName}</div>
                            <div className="text-xxs text-muted font-mono">{item.departure}</div>
                          </td>
                          <td>
                            <span className="badge bg-light text-muted border text-xxs">{item.statusName || 'Aguardando'}</span>
                          </td>
                          <td className="text-center font-mono fw-bold text-xs">{item.departure}</td>
                          <td className="text-end">
                            {isOverdue ? (
                              <span className="badge bg-danger text-white rounded-3 py-1.5 px-2.5 text-xxs animate-pulse">
                                ATRASADO : {Math.abs(item.daysLeft)} dia{Math.abs(item.daysLeft) > 1 ? 's' : ''}
                              </span>
                            ) : isCritical ? (
                              <span className="badge bg-warning text-dark rounded-3 py-1.5 px-2.5 text-xxs">
                                ENTREGA AMANHÃ
                              </span>
                            ) : (
                              <span className="badge bg-success text-white rounded-3 py-1.5 px-2.5 text-xxs">
                                {item.daysLeft} dias restantes
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted font-mono text-xs">
                        Nenhum pedido pendente com data de entrega cadastrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* MOBILE SLA LIST VIEW */}
            <div className="d-block d-md-none" id="mobile-home-sla-tracker">
              {currentStats.upcomingDeliveries && currentStats.upcomingDeliveries.length > 0 ? (
                currentStats.upcomingDeliveries.map((item, idx) => {
                  const isOverdue = item.daysLeft < 0;
                  const isCritical = item.daysLeft <= 1 || item.isEmergence;
                  return (
                    <div key={idx} className="border rounded p-2.5 mb-2 bg-light bg-opacity-40 font-sans">
                      <div className="d-flex align-items-center justify-content-between mb-1.5">
                        <span className="badge bg-secondary font-mono">Pedido #{item.number}</span>
                        <div>
                          {item.isEmergence && (
                            <span className="badge bg-danger text-white text-xxs uppercase p-1">Emergência</span>
                          )}
                          {item.isPriority && (
                            <span className="badge bg-warning text-dark text-xxs uppercase p-1 ms-1">Prioritário</span>
                          )}
                        </div>
                      </div>

                      <div className="mb-2">
                        <div className="fw-bold text-sm text-dark">{item.customerName}</div>
                        <div className="text-xxs text-muted font-mono">Entrega: {item.departure}</div>
                      </div>

                      <div className="d-flex align-items-center justify-content-between pt-1.5 border-top">
                        <span className="badge bg-white text-muted border text-xxs py-1 px-2">Setor: {item.statusName || 'Aguardando'}</span>
                        <div>
                          {isOverdue ? (
                            <span className="badge bg-danger text-white text-xxs animate-pulse">
                              ATRASADO ({Math.abs(item.daysLeft)}d)
                            </span>
                          ) : isCritical ? (
                            <span className="badge bg-warning text-dark text-xxs">
                              ENTREGA AMANHÃ
                            </span>
                          ) : (
                            <span className="badge bg-success text-white text-xxs">
                              {item.daysLeft} dias rest.
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-muted font-sans text-xs py-3">
                  Nenhum pedido pendente.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3. LAYOUT VOLUME & MIX DE PRODUTOS (RESULTADOS) */}
      {activePreset === 'VOLUME_METAS' && (
        <div className="animate-fade-in font-sans">
          {/* Row 1: KPI Cards */}
          <div className="row g-4 mb-4">
            {/* Card 1: Coifas Concluídas */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border border-success border-opacity-15 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="p-3 bg-success bg-opacity-10 text-success rounded flex-shrink-0 d-flex align-items-center justify-content-center">
                    <CheckCircle size={32} strokeWidth={2.5} />
                  </div>
                  <div className="text-end">
                    <h2 className="fw-black text-success font-display m-0" style={{ fontSize: '2.5rem', lineHeight: '1' }}>
                      {currentStats.completedOrders} itens
                    </h2>
                    <span className="text-light-emphasis text-base font-sans mt-1 d-block font-bold">Volume Faturado e Expedido</span>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end pt-3 border-top border-light border-opacity-10 text-xs text-dark">
                  <div>
                    <span className="text-muted d-block font-mono text-xxs uppercase tracking-wider">Período Selecionado</span>
                    <span className="fw-bold text-dark font-sans" style={{ fontSize: '0.9rem' }}>Últimos {currentStats.dateRangeDays} dias</span>
                  </div>
                  <span className="text-success font-mono d-flex align-items-center gap-0.5" style={{ fontSize: '0.9rem' }}>
                    Em conformidade
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Meta Mensal */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="p-3 bg-primary bg-opacity-10 text-primary rounded flex-shrink-0 d-flex align-items-center justify-content-center">
                    <TrendingUp size={32} strokeWidth={2.5} />
                  </div>
                  <div className="text-end">
                    <h2 className="fw-black text-primary font-display m-0" style={{ fontSize: '2.5rem', lineHeight: '1' }}>
                      {currentStats.productionRate}%
                    </h2>
                    <span className="text-light-emphasis text-base font-sans mt-1 d-block font-bold">Atingimento de Meta Semanal</span>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end pt-3 border-top border-light border-opacity-10 text-xs text-dark">
                  <div>
                    <span className="text-muted d-block font-mono text-xxs uppercase tracking-wider">Progresso de Lançamento</span>
                    <span className="fw-bold text-dark font-sans" style={{ fontSize: '0.9rem' }}>{currentStats.completedOrders} de {currentStats.totalOrders} pedidos finalizados</span>
                  </div>
                  <span className="text-muted font-mono d-flex align-items-center gap-0.5" style={{ fontSize: '0.9rem' }}>
                    Meta Geral: 80%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Ranking de Produtos & Preenchimento de Volume de Processos */}
          <div className="row g-4 mb-4 text-dark">
            <div className="col-12 col-lg-7">
              <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 h-100">
                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                  <h5 className="fw-bold font-sans text-dark m-0" style={{ fontSize: '1rem' }}>
                    Mapeamento Volume Físico (Mix de Modelos)
                  </h5>
                  <span className="badge bg-success bg-opacity-10 text-success font-mono text-xxs">Ranking Comercial</span>
                </div>

                <div className="d-flex flex-column gap-3.5 pt-2">
                  {currentStats.topProducts.map((prod, idx) => {
                    const totalQty = currentStats.topProducts.reduce((acc, p) => acc + p.quantity, 0) || 1;
                    const percent = Math.round((prod.quantity / totalQty) * 100);
                    return (
                      <div key={idx}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-bold text-dark font-sans text-xs">{prod.name}</span>
                          <span className="fw-semibold text-muted font-mono text-xs">{prod.quantity} un. ({percent}%)</span>
                        </div>
                        <div className="progress rounded-pill bg-light" style={{ height: '8px' }}>
                          <div className="progress-bar bg-success rounded-pill" role="progressbar" style={{ width: `${percent}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5">
              <div className="card shadow-sm border border-secondary border-opacity-10 rounded bg-white p-4 h-100">
                <h5 className="fw-bold font-sans text-dark mb-4 border-bottom pb-2" style={{ fontSize: '1rem' }}>
                  Painel de Capacidade do Período
                </h5>

                <div className="d-flex flex-column justify-content-between h-100">
                  <div className="bg-light bg-opacity-50 p-3 rounded-3 mb-3 d-flex align-items-center justify-content-between border">
                    <div>
                      <span className="text-xxs text-muted d-block font-mono uppercase">Pendência</span>
                      <span className="fw-black text-danger text-lg font-mono">{currentStats.pendingOrders} pedidos</span>
                    </div>
                    <Wrench size={22} className="text-danger" />
                  </div>

                  <div className="bg-light bg-opacity-50 p-3 rounded-3 mb-3 d-flex align-items-center justify-content-between border">
                    <div>
                      <span className="text-xxs text-muted d-block font-mono uppercase">Conclusão de Encomenda</span>
                      <span className="fw-black text-success text-lg font-mono">{currentStats.completedOrders} coifas</span>
                    </div>
                    <CheckCircle size={22} className="text-success" />
                  </div>

                  <div className="bg-light bg-opacity-50 p-3 rounded-3 d-flex align-items-center justify-content-between border">
                    <div>
                      <span className="text-xxs text-muted d-block font-mono uppercase">Taxa de Ocupação Média</span>
                      <span className="fw-black text-primary text-lg font-mono">100% ativa</span>
                    </div>
                    <Activity size={22} className="text-primary" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Row 3: Smaller status list cards (Bottom Cards) - SEMPRE SINALIZANDO O CHÃO DE FÁBRICA ATIVO */}
      <div className="row g-4 font-sans text-dark mb-4 card-group-deck">
        {/* Card 1: Pedidos Cadastrados */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-3.5 rounded h-100">
            <div className="d-flex align-items-center gap-3">
              <div className="p-2 bg-primary bg-opacity-10 text-primary rounded">
                <Wrench size={22} strokeWidth={2.5} />
              </div>
              <div className="lh-tight">
                <span className="fw-black text-dark font-display d-block" style={{ fontSize: '1.4rem' }}>
                  {currentStats.totalOrders} pedidos
                </span>
                <span className="text-secondary text-xs font-sans uppercase font-bold tracking-wider opacity-90">Cadastrados em andamento</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Em Produção */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-3.5 rounded h-100">
            <div className="d-flex align-items-center gap-3">
              <div className="p-2 bg-danger bg-opacity-10 text-danger rounded">
                <Wrench size={22} strokeWidth={2.5} />
              </div>
              <div className="lh-tight">
                <span className="fw-black text-dark font-display d-block" style={{ fontSize: '1.4rem' }}>
                  {currentStats.pendingOrders} pedidos
                </span>
                <span className="text-secondary text-xs font-sans uppercase font-bold tracking-wider opacity-90">Em Linha de Montagem</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Finalizados */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-3.5 rounded h-100">
            <div className="d-flex align-items-center gap-3">
              <div className="p-2 bg-success bg-opacity-10 text-success rounded">
                <Wrench size={22} strokeWidth={2.5} />
              </div>
              <div className="lh-tight">
                <span className="fw-black text-dark font-display d-block" style={{ fontSize: '1.4rem' }}>
                  {currentStats.completedOrders} pedidos
                </span>
                <span className="text-secondary text-xs font-sans uppercase font-bold tracking-wider opacity-90">Finalizados e Expedidos</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
